import type { MessagePayload } from 'firebase/messaging'

export const FOREGROUND_NOTIFICATION_APP_NAME = 'Driver 360 Portal'

interface PushPayloadContent {
  title?: string
  subtitle?: string
  body?: string
}

interface PushPayloadAction {
  style?: 'primary' | 'secondary'
  action?: {
    type?: 'deeplink' | 'external_url' | 'close'
    value?: string
  }
}

interface PushPayload {
  content?: PushPayloadContent
  actions?: PushPayloadAction[]
}

export interface ForegroundNotificationData {
  title: string
  summary?: string
  detail?: string
  target?: string
}

const parsePushPayload = (serializedPayload?: string): PushPayload | null => {
  if (!serializedPayload) return null

  try {
    const payload = JSON.parse(serializedPayload)
    return payload && typeof payload === 'object'
      ? (payload as PushPayload)
      : null
  } catch {
    return null
  }
}

const getActionTarget = (payload: PushPayload | null) => {
  const actions = payload?.actions ?? []
  const action =
    actions.find(
      (item) =>
        item.style === 'primary' &&
        item.action?.type !== 'close' &&
        item.action?.value,
    ) ??
    actions.find((item) => item.action?.type !== 'close' && item.action?.value)

  return action?.action?.value
}

export const toForegroundNotificationData = (
  message: MessagePayload,
): ForegroundNotificationData => {
  const payload = parsePushPayload(message.data?.payload)
  const bodyLines = message.notification?.body
    ?.split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
  const payloadSubtitle = payload?.content?.subtitle?.trim()
  const payloadBody = payload?.content?.body?.trim()
  const remainingBody = bodyLines?.slice(1).join(' ').trim()
  const summary = bodyLines?.at(0) ?? payloadSubtitle ?? payloadBody
  const detail = [remainingBody, payloadSubtitle, payloadBody].find(
    (line) => line && line !== summary,
  )

  return {
    title:
      message.notification?.title ??
      payload?.content?.title ??
      'New notification',
    ...(summary && { summary }),
    ...(detail && { detail }),
    ...(message.fcmOptions?.link ||
    message.data?.link ||
    getActionTarget(payload)
      ? {
          target:
            message.fcmOptions?.link ??
            message.data?.link ??
            getActionTarget(payload),
        }
      : {}),
  }
}

export const getSafeNotificationTarget = (target?: string): URL | null => {
  if (!target) return null

  try {
    const url = new URL(target, window.location.origin)
    return url.protocol === 'http:' || url.protocol === 'https:' ? url : null
  } catch {
    return null
  }
}
